import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useCallback, useState } from 'react';
import { fetchHalachicData, refreshHalachicData } from '../store/actions/halachicEngineActions';

/**
 * Convenience hook for consuming halachic engine data.
 * Auto-fetches on mount and auto-refreshes at sunset + midnight.
 *
 * @param {object} config - engine config (location, isIsrael, edah, etc.)
 * @param {object} [options] - { autoFetch: true }
 * @returns {{ today, tomorrow, loading, error, refresh, lastUpdated }}
 */
export function useHalachicData(config, options = { autoFetch: true }) {
  const dispatch = useDispatch();
  const { today, tomorrow, loading, error, lastUpdated } = useSelector(
    (state) => state.halachicEngine
  );
  const [refreshTick, setRefreshTick] = useState(0);

  // Initial fetch + re-fetch on config change or refreshTick
  useEffect(() => {
    if (options.autoFetch && config?.location?.lat && config?.location?.lng) {
      dispatch(fetchHalachicData(config));
    }
  }, [
    config?.location?.lat,
    config?.location?.lng,
    config?.isIsrael,
    config?.edah,
    refreshTick,
  ]);

  // Auto-refresh at sunset + midnight
  useEffect(() => {
    const now = Date.now();
    const timers = [];

    // Schedule sunset refresh from zmanim data
    if (today?.zmanim?.sunset) {
      const [h, m] = today.zmanim.sunset.split(':').map(Number);
      const sunsetDt = new Date();
      sunsetDt.setHours(h, m, 0, 0);
      const sunsetMs = sunsetDt.getTime();
      const targetMs = sunsetMs > now ? sunsetMs : sunsetMs + 86400000;
      if (targetMs > now) {
        timers.push(setTimeout(() => setRefreshTick(t => t + 1), targetMs - now + 63000));
      }
    }

    // Midnight refresh
    const nextMidnight = new Date();
    nextMidnight.setDate(nextMidnight.getDate() + 1);
    nextMidnight.setHours(0, 0, 15, 0);
    timers.push(setTimeout(() => setRefreshTick(t => t + 1), nextMidnight - now));

    return () => timers.forEach(clearTimeout);
  }, [today?.zmanim?.sunset, today?.date]);

  // Guard: detect missed sunset/midnight refreshes (sleep/wake, throttling)
  useEffect(() => {
    const guard = setInterval(() => {
      const now = new Date();

      // After sunset but data still pre-sunset → missed refresh
      if (!today?.isAfterSunset && today?.zmanim?.sunset && today.zmanim.sunset !== '--:--') {
        const [h, m] = today.zmanim.sunset.split(':').map(Number);
        const sunset = new Date();
        sunset.setHours(h, m + 1, 3, 0);
        if (now > sunset) {
          setRefreshTick(t => t + 1);
          return;
        }
      }

      // Past midnight but data from yesterday
      if (today?.date && !today?.isAfterSunset) {
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        if (`${yyyy}-${mm}-${dd}` !== today.date) {
          setRefreshTick(t => t + 1);
        }
      }
    }, 60_000);

    return () => clearInterval(guard);
  }, [today?.isAfterSunset, today?.zmanim?.sunset, today?.date]);

  const refresh = useCallback(() => {
    dispatch(refreshHalachicData(config));
  }, [dispatch, config]);

  return { today, tomorrow, loading, error, lastUpdated, refresh };
}
